import { useParams, Link, useNavigate } from 'react-router';
import {
  Plus,
  ArrowLeft,
  Trash2,
  FileText,
  Play,
  HelpCircle,
  Users,
  MessageSquare,
  Zap,
  ExternalLink,
  ChevronRight,
  BarChart3,
  Bookmark,
  Search,
  MoreVertical,
  Clock,
  AlertCircle,
  Pencil,
  Loader2,
  FolderPlus,
  Layers,
  ChevronsUpDown,
  ChevronDown,
  Camera,
  Check,
  Copy,
  Sparkles,
  Globe,
  ShieldCheck,
  Fingerprint,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { useWhatsAppGroups } from '../hooks/useWhatsAppGroups';
import { useImportMaterial } from '../hooks/useMaterials';
import {
  useProgram,
  useDeleteLevel,
  usePrograms,
  useSyncProgram,
  useUpdateProgram,
} from '../hooks/usePrograms';
import { useDeleteStage } from '../hooks/useProgramStages';
import { useDeleteSubject, useReorderSubjects } from '../hooks/useProgramSubjects';
import { useDeleteSession } from '../hooks/useLessonSessions';
import { Button, Card, LoadingSpinner } from '@lms/ui';
import { MaterialModal } from '../components/MaterialModal';
import { LevelModal } from '../components/LevelModal';
import { FlowModal } from '../components/FlowModal';
import { StageModal } from '../components/StageModal';
import { SubjectModal } from '../components/SubjectModal';
import { SessionModal } from '../components/SessionModal';
import { BulkSessionModal } from '../components/BulkSessionModal';
import { ProgramModal } from '../components/ProgramModal';
import { DuplicateProgramModal } from '../components/DuplicateProgramModal';
import { WhatsAppGroupModal } from '../components/WhatsAppGroupModal';
import { ManualEnrollModal } from '../components/ManualEnrollModal';
import { SubjectCurriculumView } from '../components/SubjectCurriculumView';
import { useNotificationStore } from '../store/notifications';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { useEnrollmentPage } from '../hooks/useEnrollments';
import { UserDetailDrawer } from '../components/UserDetailDrawer';
import { useRegistrations } from '../hooks/useRegistrations';
import { useLanguage } from '../lib/LanguageContext';
import { Pagination } from '../components/Pagination';
import { resolveMediaUrl } from '@lms/shared/utils';
import { apiBaseUrl } from '../lib/api-client';

import type {
  Material,
  Program,
  Evaluation,
  WhatsAppGroup,
  ProgramLevel,
  ProgramStage,
  ProgramSubject,
  LessonSession,
  LearningFlow,
} from '@lms/shared/types';

type TabType = 'CURRICULUM' | 'WHATSAPP' | 'OVERVIEW' | 'MATERIALS' | 'EVALUATIONS';

type EvaluationWithMaterial = Evaluation & { material?: Material };

export function ProgramDetail() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('CURRICULUM');
  const [participantsSubTab, setParticipantsSubTab] = useState<'ACTIVE' | 'PENDING'>('ACTIVE');
  const [participantPage, setParticipantPage] = useState(1);
  const [isManualEnrollModalOpen, setIsManualEnrollModalOpen] = useState(false);
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [isFlowModalOpen, setIsFlowModalOpen] = useState(false);

  // Learning Path 6-Tier Modals State
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [selectedStageLevelId, setSelectedStageLevelId] = useState('');
  const [selectedStageLevelName, setSelectedStageLevelName] = useState('');
  const [editingStage, setEditingStage] = useState<ProgramStage | undefined>();

  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [selectedSubjectStageId, setSelectedSubjectStageId] = useState('');
  const [selectedSubjectStageName, setSelectedSubjectStageName] = useState('');
  const [editingSubject, setEditingSubject] = useState<ProgramSubject | undefined>();

  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [selectedSessionSubjectId, setSelectedSessionSubjectId] = useState('');
  const [selectedSessionSubjectName, setSelectedSessionSubjectName] = useState('');
  const [editingSession, setEditingSession] = useState<LessonSession | undefined>();

  const [isBulkSessionModalOpen, setIsBulkSessionModalOpen] = useState(false);
  const [selectedBulkSubjectId, setSelectedBulkSubjectId] = useState('');
  const [selectedBulkSubjectName, setSelectedBulkSubjectName] = useState('');

  const [isProgramModalOpen, setIsProgramModalOpen] = useState(false);
  const [isDuplicateProgramModalOpen, setIsDuplicateProgramModalOpen] = useState(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [editingWhatsAppGroup, setEditingWhatsAppGroup] = useState<WhatsAppGroup | undefined>();
  const [editingMaterial, setEditingMaterial] = useState<Material | undefined>();
  const [searchMaterialQuery, setSearchMaterialQuery] = useState('');
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');
  const [selectedLevelId, setSelectedLevelId] = useState<string>('');
  const [editingLevel, setEditingLevel] = useState<ProgramLevel | undefined>();
  const [editingFlow, setEditingFlow] = useState<LearningFlow | undefined>();
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const deleteLevelMutation = useDeleteLevel();
  const deleteStageMutation = useDeleteStage();
  const deleteSubjectMutation = useDeleteSubject();
  const reorderSubjectsMutation = useReorderSubjects();
  const deleteSessionMutation = useDeleteSession();
  const importMaterialMutation = useImportMaterial(id);
  const updateProgramMutation = useUpdateProgram();
  const [copiedLink, setCopiedLink] = useState(false);
  const { data: allProgramsData } = usePrograms({ limit: 100 });

  const [isImportMaterialOpen, setIsImportMaterialOpen] = useState(false);
  const [importTargetFlowId] = useState('');
  const [selectedSourceProgramId, setSelectedSourceProgramId] = useState('');
  const [selectedSourceFlowId, setSelectedSourceFlowId] = useState('');
  const [selectedSourceMaterialId, setSelectedSourceMaterialId] = useState('');

  const { data: sourceProgramDetail } = useProgram(selectedSourceProgramId);

  const { addNotification } = useNotificationStore();

  const { data: program, isLoading, error } = useProgram(id || '');
  const { data: whatsappGroups } = useWhatsAppGroups({ programId: id });

  // Data fetching for participants
  const { data: activeEnrollmentsData, isLoading: isLoadingEnrollments } = useEnrollmentPage({
    programId: id,
    page: participantPage,
    limit: 50,
  });
  const activeEnrollments = activeEnrollmentsData?.items ?? [];
  const { data: pendingRegistrationsData, isLoading: isLoadingPending } = useRegistrations({
    programId: id,
    status: 'PENDING',
  });

  const syncProgramMutation = useSyncProgram();

  const averageProgress = useMemo(() => {
    return activeEnrollmentsData?.summary.averageProgress ?? 0;
  }, [activeEnrollmentsData]);

  const allSubjectsInProgram = useMemo(() => {
    return program?.levels?.flatMap(l => l.stages?.flatMap(s => s.subjects || []) || []) || [];
  }, [program?.levels]);

  const persistSubjectOrder = (subjects: ProgramSubject[]) => {
    reorderSubjectsMutation.mutate(
      {
        programId: id || '',
        items: subjects.map((subject, index) => ({
          id: subject.id,
          order: index + 1,
          expectedOrder: subject.order,
        })),
      },
      {
        onSuccess: () =>
          addNotification({
            type: 'success',
            title: t(copy.learningPathStudio.fixSubjectOrder),
            message: t(copy.learningPathStudio.fixSubjectOrderSuccess),
          }),
        onError: () =>
          addNotification({
            type: 'error',
            title: t(copy.learningPathStudio.fixSubjectOrder),
            message: t(copy.learningPathStudio.fixSubjectOrderError),
          }),
      }
    );
  };

  const filteredMaterials = useMemo(() => {
    const allMaterials = [
      ...(program?.levels?.flatMap(l => l.flows || [])?.flatMap(f => f.materials || []) || []),
      ...(program?.levels
        ?.flatMap(l => l.stages || [])
        ?.flatMap(s => s.subjects || [])
        ?.flatMap(sub => sub.sessions || [])
        ?.flatMap(ses => ses.materials || []) || []),
    ];
    if (!searchMaterialQuery) return allMaterials;
    return allMaterials.filter(
      m =>
        m.title.toLowerCase().includes(searchMaterialQuery.toLowerCase()) ||
        (m.description && m.description.toLowerCase().includes(searchMaterialQuery.toLowerCase()))
    );
  }, [program?.levels, searchMaterialQuery]);

  const [collapsedLevels, setCollapsedLevels] = useState<Record<string, boolean>>({});
  const [collapsedStages, setCollapsedStages] = useState<Record<string, boolean>>({});

  const toggleLevelCollapse = (levelId: string) => {
    setCollapsedLevels(prev => ({ ...prev, [levelId]: !prev[levelId] }));
  };

  const toggleStageCollapse = (stageId: string) => {
    setCollapsedStages(prev => ({ ...prev, [stageId]: !prev[stageId] }));
  };

  const isAllCollapsed = useMemo(() => {
    const levels = program?.levels || [];
    if (levels.length === 0) return false;
    return levels.every(l => !!collapsedLevels[l.id]);
  }, [program?.levels, collapsedLevels]);

  const toggleAllCollapse = () => {
    if (isAllCollapsed) {
      setCollapsedLevels({});
      setCollapsedStages({});
    } else {
      const levels = program?.levels || [];
      const newL = levels.reduce<Record<string, boolean>>((acc, l) => {
        acc[l.id] = true;
        return acc;
      }, {});
      const newS = levels
        .flatMap(l => l.stages || [])
        .reduce<Record<string, boolean>>((acc, s) => {
          acc[s.id] = true;
          return acc;
        }, {});
      setCollapsedLevels(newL);
      setCollapsedStages(newS);
    }
  };

  const handleDeleteLevel = (levelId: string) => {
    if (confirm(t(copy.programDetail.confirmDeletePhase))) {
      deleteLevelMutation.mutate({ programId: id!, levelId });
    }
  };

  const handleDeleteStage = (stageId: string) => {
    if (confirm(t(copy.learningPathStudio.deleteStageConfirm))) {
      deleteStageMutation.mutate({ id: stageId, programId: id! });
    }
  };

  const handleDeleteSubject = (subjectId: string) => {
    if (confirm(t(copy.learningPathStudio.deleteSubjectConfirm))) {
      deleteSubjectMutation.mutate({ id: subjectId, programId: id! });
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    if (confirm(t(copy.learningPathStudio.deleteSessionConfirm))) {
      deleteSessionMutation.mutate({ id: sessionId, programId: id! });
    }
  };

  const getMaterialIcon = (type: string) => {
    switch (type) {
      case 'VIDEO':
        return <Play size={14} />;
      case 'DOCUMENT':
        return <FileText size={14} />;
      case 'QUIZ':
        return <HelpCircle size={14} className="text-secondary" />;
      case 'REFLECTION':
        return <MessageSquare size={14} />;
      case 'DISCUSSION':
        return <Users size={14} />;
      default:
        return <FileText size={14} />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <LoadingSpinner size="lg" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.programDetail.loadingFramework} />
        </p>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="bg-error-container text-on-error-container p-10 rounded-[2rem] animate-fadeIn border border-error/20">
        <h3 className="text-2xl font-headline font-bold mb-4 flex items-center gap-3">
          <AlertCircle size={28} />
          <Bilingual {...copy.programDetail.notFoundTitle} />
        </h3>
        <p className="text-base font-body opacity-80 leading-relaxed">
          <Bilingual {...copy.programDetail.notFoundDesc} />
        </p>
        <Link
          to="/programs"
          className="inline-flex items-center gap-3 mt-8 bg-error text-on-error px-8 py-3 rounded-xl font-label font-bold text-xs uppercase tracking-widest hover:opacity-90 active:scale-95 transition-all"
        >
          <ArrowLeft size={16} />
          <Bilingual {...copy.programDetail.btnReturnPortfolio} />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-12 animate-fadeIn pb-12">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60">
        <Link to="/" className="hover:text-primary transition-colors">
          <Bilingual {...copy.programDetail.breadcrumbPortfolio} />
        </Link>
        <ChevronRight size={10} />
        <Link to="/programs" className="hover:text-primary transition-colors">
          <Bilingual {...copy.programDetail.breadcrumbAcademic} />
        </Link>
        <ChevronRight size={10} />
        <span className="text-on-surface font-black">{program.name}</span>
      </nav>

      {/* Program Header & Cover Banner */}
      <section className="space-y-4">
        {program.coverImage ? (
          <div className="relative rounded-[2rem] overflow-hidden aspect-[21/9] sm:aspect-[24/7] max-h-72 w-full shadow-level3 group bg-surface-container-low">
            <img
              src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
              alt={program.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent flex flex-col justify-end p-6 sm:p-10">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="space-y-1 text-white">
                  <div className="flex items-center gap-2 text-primary-fixed font-bold text-[10px] uppercase tracking-[0.25em] font-label">
                    <Zap size={14} className="text-secondary" />
                    <Bilingual {...copy.programDetail.headerEyebrow} />
                    <span className="font-mono px-2 py-0.5 rounded-full bg-white/10 backdrop-blur-xs text-[10px]">
                      {program.code}
                    </span>
                  </div>
                  <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold font-headline tracking-tight text-white m-0">
                    {program.name}
                  </h1>
                </div>
                <button
                  type="button"
                  onClick={() => setIsProgramModalOpen(true)}
                  className="px-4 py-2.5 rounded-xl bg-white/20 hover:bg-white/30 backdrop-blur-md text-white font-label font-bold text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-md active:scale-95"
                >
                  <Camera size={14} />
                  <span>Ubah Cover</span>
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {!program.coverImage && (
            <div className="flex items-center gap-2 text-primary font-bold text-[10px] uppercase tracking-[0.25em] font-label">
              <Zap size={14} className="text-secondary" />
              <Bilingual {...copy.programDetail.headerEyebrow} />
            </div>
          )}

          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 md:gap-0">
            <div className="space-y-2">
              {!program.coverImage && (
                <h1 className="text-3xl md:text-5xl font-extrabold font-headline tracking-tighter text-on-surface m-0 leading-tight md:leading-none">
                  {program.name}
                </h1>
              )}
              <p className="text-sm md:text-xl font-body text-on-surface-variant mt-2 leading-relaxed max-w-2xl italic opacity-80">
                {program.description || t(copy.programDetail.defaultDescription)}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 md:gap-4 w-full md:w-auto mt-4 md:mt-0">
              <Button
                variant="ghost"
                onClick={() => setIsProgramModalOpen(true)}
                className="flex-1 md:flex-none px-4 md:px-8 border border-outline-variant/30 font-bold text-[10px] uppercase tracking-widest rounded-xl"
              >
                <Bilingual {...copy.programDetail.btnEditDossier} />
              </Button>
              <Button
                variant="secondary"
                onClick={() => setIsDuplicateProgramModalOpen(true)}
                className="flex-1 md:flex-none px-4 md:px-6 font-bold text-[10px] uppercase tracking-widest rounded-xl"
              >
                <Copy size={14} />
                <Bilingual {...copy.programDetail.btnDuplicateProgram} />
              </Button>
              <Button
                onClick={() => {
                  syncProgramMutation.mutate(id || '', {
                    onSuccess: () => {
                      addNotification({
                        type: 'success',
                        title: t(copy.programDetail.syncFeatureName),
                        message: 'Berhasil menyelaraskan progress pembelajaran peserta',
                      });
                    },
                    onError: (err: unknown) => {
                      addNotification({
                        type: 'error',
                        title: 'Sync Failed',
                        message:
                          err instanceof Error
                            ? err.message
                            : 'An error occurred during synchronization',
                      });
                    },
                  });
                }}
                disabled={syncProgramMutation.isPending}
                className="flex-1 md:flex-none px-4 md:px-8 shadow-level2 font-bold text-[10px] uppercase tracking-[0.2em] rounded-xl flex items-center gap-2 justify-center"
              >
                {syncProgramMutation.isPending ? (
                  <Loader2 className="animate-spin" size={12} />
                ) : null}
                <Bilingual {...copy.programDetail.btnExecuteSync} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Tabbed Navigation */}
      <div className="mb-10 w-full overflow-hidden">
        <div className="flex gap-6 md:gap-12 border-b border-outline-variant/10 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { id: 'CURRICULUM', label: copy.programDetail.tabCurriculum },
            { id: 'EVALUATIONS', label: copy.programDetail.tabEvaluations },
            { id: 'WHATSAPP', label: copy.programDetail.tabWhatsApp },
            { id: 'OVERVIEW', label: copy.programDetail.tabOverview },
            { id: 'MATERIALS', label: copy.programDetail.tabMaterials },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`pb-5 px-1 text-xs font-black uppercase tracking-[0.25em] transition-all relative group
                ${
                  activeTab === tab.id
                    ? 'text-primary'
                    : 'text-on-surface-variant hover:text-primary opacity-40 hover:opacity-100'
                }`}
            >
              <Bilingual {...tab.label} />
              {activeTab === tab.id && (
                <div className="absolute -bottom-[1px] left-0 right-0 h-0.5 bg-primary rounded-full animate-fadeIn" />
              )}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'CURRICULUM' ? (
        <div className="grid grid-cols-1 gap-8 items-start xl:grid-cols-[minmax(0,1fr)_18rem] 2xl:grid-cols-[minmax(0,1fr)_20rem]">
          {/* Main Flow Builder */}
          <div className="min-w-0 space-y-8">
            <div className="bg-surface-container-lowest rounded-[2rem] p-6 md:p-10 shadow-level1 border border-on-surface/5">
              {/* Studio Header Toolbar */}
              <div className="flex flex-wrap justify-between items-center gap-4 mb-8 pb-6 border-b border-outline-variant/10">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-2xl font-headline font-bold text-on-surface m-0 tracking-tight">
                      <Bilingual {...copy.learningPathStudio.tabTitle} />
                    </h3>
                    <span className="px-3 py-1 bg-primary/10 text-primary text-[10px] font-black uppercase tracking-wider rounded-full">
                      6-Tier Studio
                    </span>
                  </div>
                  <p className="text-sm font-body text-on-surface-variant opacity-70">
                    <Bilingual {...copy.learningPathStudio.tabSubtitle} />
                  </p>
                </div>

                <div className="flex items-center gap-2.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={toggleAllCollapse}
                    className="rounded-xl px-4 py-2 font-bold text-[10px] uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <ChevronsUpDown size={14} />
                    <span>{isAllCollapsed ? 'Perluas Semua' : 'Ciutkan Semua'}</span>
                  </Button>

                  <Button
                    onClick={() => {
                      setEditingLevel(undefined);
                      setIsLevelModalOpen(true);
                    }}
                    size="sm"
                    className="rounded-xl px-5 py-2 font-bold text-[10px] uppercase tracking-widest flex items-center gap-1.5 shadow-level2"
                  >
                    <Plus size={15} />
                    <Bilingual {...copy.learningPathStudio.addLevel} />
                  </Button>
                </div>
              </div>

              {/* Levels Accordion List */}
              <div className="space-y-10">
                {program.levels && program.levels.length > 0 ? (
                  program.levels.map(level => {
                    const levelStages = level.stages || [];
                    const levelSubjectsCount = levelStages.reduce(
                      (acc, s) => acc + (s.subjects?.length || 0),
                      0
                    );
                    const levelSessionsCount = levelStages.reduce(
                      (acc, s) =>
                        acc +
                        (s.subjects?.reduce((sum, sub) => sum + (sub.sessions?.length || 0), 0) ||
                          0),
                      0
                    );
                    const isLevelCollapsed = !!collapsedLevels[level.id];

                    return (
                      <div key={level.id} className="relative space-y-6">
                        {/* Level Header Banner */}
                        <div className="flex flex-wrap items-center justify-between gap-4 bg-surface-container-low/70 p-5 md:p-6 rounded-3xl group hover:bg-surface-container-low transition-colors">
                          <div className="flex items-center gap-3.5 flex-1 min-w-[240px]">
                            <button
                              type="button"
                              onClick={() => toggleLevelCollapse(level.id)}
                              className="p-1.5 rounded-xl hover:bg-surface-container-high text-on-surface-variant/70 hover:text-primary transition-colors shrink-0"
                              title={isLevelCollapsed ? 'Buka Level' : 'Ciutkan Level'}
                            >
                              {isLevelCollapsed ? (
                                <ChevronRight size={22} />
                              ) : (
                                <ChevronDown size={22} />
                              )}
                            </button>

                            <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center text-on-primary font-headline font-black text-lg shadow-level2 shrink-0">
                              {level.order}
                            </div>

                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[9px] font-label font-black text-primary uppercase tracking-[0.2em]">
                                  Level / Tingkat {String(level.order).padStart(2, '0')}
                                </span>
                                <span className="text-[9px] text-on-surface-variant/60 font-bold">
                                  • {levelStages.length} Tahapan • {levelSubjectsCount} Mata
                                  Pelajaran • {levelSessionsCount} Pertemuan
                                </span>
                              </div>

                              <h4
                                onClick={() => toggleLevelCollapse(level.id)}
                                className="text-lg md:text-xl font-headline font-bold text-on-surface m-0 cursor-pointer hover:text-primary transition-colors"
                              >
                                {level.name}
                              </h4>
                            </div>
                          </div>

                          {/* Level Action Buttons */}
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedStageLevelId(level.id);
                                setSelectedStageLevelName(level.name);
                                setEditingStage(undefined);
                                setIsStageModalOpen(true);
                              }}
                              className="rounded-xl px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-sm"
                            >
                              <Plus size={14} />
                              <Bilingual {...copy.learningPathStudio.addStage} />
                            </Button>

                            <button
                              type="button"
                              onClick={() => {
                                setEditingLevel(level);
                                setIsLevelModalOpen(true);
                              }}
                              className="p-2 hover:bg-surface-container-high rounded-xl text-on-surface-variant/70 hover:text-primary transition-colors"
                              title="Edit Level"
                            >
                              <Pencil size={16} />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteLevel(level.id)}
                              disabled={deleteLevelMutation.isPending}
                              className="p-2 hover:bg-surface-container-high rounded-xl text-on-surface-variant/70 hover:text-error transition-colors disabled:opacity-50"
                              title="Hapus Level"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        {/* Level Body (Stages) */}
                        {!isLevelCollapsed && (
                          <div className="ml-4 md:ml-8 pl-4 md:pl-6 border-l-2 border-dashed border-outline-variant/20 space-y-6">
                            {levelStages.length > 0 ? (
                              levelStages.map(stage => {
                                const stageSubjects = [...(stage.subjects || [])].sort(
                                  (left, right) => left.order - right.order
                                );
                                const needsSubjectOrderRepair = stageSubjects.some(
                                  (subject, index) => subject.order !== index + 1
                                );
                                const stageSessionsCount = stageSubjects.reduce(
                                  (sum, sub) => sum + (sub.sessions?.length || 0),
                                  0
                                );
                                const isStageCollapsed = !!collapsedStages[stage.id];

                                return (
                                  <div
                                    key={stage.id}
                                    className="bg-surface-container-low/40 rounded-3xl p-5 md:p-6 border border-outline-variant/10 shadow-sm space-y-6"
                                  >
                                    {/* Stage Header */}
                                    <div className="flex flex-wrap justify-between items-center gap-4 pb-4 border-b border-outline-variant/10">
                                      <div className="flex items-center gap-3.5 flex-1 min-w-[240px]">
                                        <button
                                          type="button"
                                          onClick={() => toggleStageCollapse(stage.id)}
                                          className="p-1 rounded-lg hover:bg-surface-container-high text-on-surface-variant/70 hover:text-primary transition-colors shrink-0"
                                          title={isStageCollapsed ? 'Buka Tahap' : 'Ciutkan Tahap'}
                                        >
                                          {isStageCollapsed ? (
                                            <ChevronRight size={18} />
                                          ) : (
                                            <ChevronDown size={18} />
                                          )}
                                        </button>

                                        <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 shadow-sm">
                                          {stage.stageCode || 'T'}
                                        </div>

                                        <div className="space-y-1 flex-1">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-xs font-black uppercase tracking-wider text-primary">
                                              {stage.stageCode
                                                ? `Tahap ${stage.stageCode}`
                                                : 'Tahapan'}
                                            </span>
                                            <span
                                              className={`px-2 py-0.5 text-[8px] font-black uppercase rounded-full ${
                                                stage.isSequential
                                                  ? 'bg-primary/10 text-primary'
                                                  : 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300'
                                              }`}
                                            >
                                              {stage.isSequential
                                                ? t(copy.learningPathStudio.badgeSequential)
                                                : t(copy.learningPathStudio.badgeFlexible)}
                                            </span>
                                            <span className="px-2.5 py-0.5 text-[9px] font-bold uppercase rounded-full bg-secondary/15 text-secondary border border-secondary/25">
                                              KKM Tahap: {stage.passingScore ?? 70}%
                                            </span>
                                            <span className="text-[9px] text-on-surface-variant/60 font-bold">
                                              • {stageSubjects.length} Mapel • {stageSessionsCount}{' '}
                                              Pertemuan
                                            </span>
                                          </div>

                                          <h5
                                            onClick={() => toggleStageCollapse(stage.id)}
                                            className="text-base md:text-lg font-headline font-bold text-on-surface m-0 cursor-pointer hover:text-primary transition-colors"
                                          >
                                            {stage.name}
                                          </h5>
                                        </div>
                                      </div>

                                      {/* Stage Action Buttons */}
                                      <div className="flex items-center gap-2 shrink-0">
                                        {needsSubjectOrderRepair && (
                                          <Button
                                            size="sm"
                                            variant="secondary"
                                            disabled={reorderSubjectsMutation.isPending}
                                            onClick={() => persistSubjectOrder(stageSubjects)}
                                            className="rounded-xl px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider"
                                          >
                                            <ChevronsUpDown size={13} />
                                            <Bilingual
                                              {...copy.learningPathStudio.fixSubjectOrder}
                                            />
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          onClick={() => {
                                            setSelectedSubjectStageId(stage.id);
                                            setSelectedSubjectStageName(stage.name);
                                            setEditingSubject(undefined);
                                            setIsSubjectModalOpen(true);
                                          }}
                                          className="rounded-xl px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-sm"
                                        >
                                          <Plus size={13} />
                                          <Bilingual {...copy.learningPathStudio.addSubject} />
                                        </Button>

                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSelectedStageLevelId(level.id);
                                            setSelectedStageLevelName(level.name);
                                            setEditingStage(stage);
                                            setIsStageModalOpen(true);
                                          }}
                                          className="p-2 hover:bg-surface-container-high rounded-xl text-on-surface-variant/60 hover:text-primary transition-colors"
                                          title="Edit Tahap"
                                        >
                                          <Pencil size={15} />
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() => handleDeleteStage(stage.id)}
                                          disabled={deleteStageMutation.isPending}
                                          className="p-2 hover:bg-surface-container-high rounded-xl text-on-surface-variant/60 hover:text-error transition-colors disabled:opacity-50"
                                          title="Hapus Tahap"
                                        >
                                          <Trash2 size={15} />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Stage Description */}
                                    {stage.description && (
                                      <p className="text-xs text-on-surface-variant/70 -mt-2">
                                        {stage.description}
                                      </p>
                                    )}

                                    {/* Stage Subjects List */}
                                    {!isStageCollapsed && (
                                      <div className="space-y-4">
                                        {stageSubjects.length > 0 ? (
                                          stageSubjects.map((subject, subjectIndex) => (
                                            <SubjectCurriculumView
                                              key={subject.id}
                                              subject={subject}
                                              stageId={stage.id}
                                              stageName={stage.name}
                                              canMoveUp={subjectIndex > 0}
                                              canMoveDown={subjectIndex < stageSubjects.length - 1}
                                              isReordering={reorderSubjectsMutation.isPending}
                                              onMoveUp={() => {
                                                const reordered = [...stageSubjects];
                                                [
                                                  reordered[subjectIndex - 1],
                                                  reordered[subjectIndex],
                                                ] = [
                                                  reordered[subjectIndex],
                                                  reordered[subjectIndex - 1],
                                                ];
                                                persistSubjectOrder(reordered);
                                              }}
                                              onMoveDown={() => {
                                                const reordered = [...stageSubjects];
                                                [
                                                  reordered[subjectIndex],
                                                  reordered[subjectIndex + 1],
                                                ] = [
                                                  reordered[subjectIndex + 1],
                                                  reordered[subjectIndex],
                                                ];
                                                persistSubjectOrder(reordered);
                                              }}
                                              onAddSession={(subId, subName) => {
                                                setSelectedSessionSubjectId(subId);
                                                setSelectedSessionSubjectName(subName);
                                                setEditingSession(undefined);
                                                setIsSessionModalOpen(true);
                                              }}
                                              onBulkGenerate={(subId, subName) => {
                                                setSelectedBulkSubjectId(subId);
                                                setSelectedBulkSubjectName(subName);
                                                setIsBulkSessionModalOpen(true);
                                              }}
                                              onEditSubject={sub => {
                                                setSelectedSubjectStageId(stage.id);
                                                setSelectedSubjectStageName(stage.name);
                                                setEditingSubject(sub);
                                                setIsSubjectModalOpen(true);
                                              }}
                                              onDeleteSubject={subId => handleDeleteSubject(subId)}
                                              onCreateSubjectEvaluation={subjectToAssess =>
                                                void navigate('/evaluations/create', {
                                                  state: {
                                                    programId: id,
                                                    subjectId: subjectToAssess.id,
                                                    subjectName: subjectToAssess.name,
                                                    passingScore:
                                                      subjectToAssess.passingScore ?? 70,
                                                  },
                                                })
                                              }
                                              onEditSession={(sess, subId, subName) => {
                                                setSelectedSessionSubjectId(subId);
                                                setSelectedSessionSubjectName(subName);
                                                setEditingSession(sess);
                                                setIsSessionModalOpen(true);
                                              }}
                                              onCreateSessionEvaluation={(session, subjectName) =>
                                                void navigate('/evaluations/create', {
                                                  state: {
                                                    programId: id,
                                                    sessionId: session.id,
                                                    sessionTitle: session.title,
                                                    subjectName,
                                                    passingScore: subject.passingScore ?? 70,
                                                  },
                                                })
                                              }
                                              onDeleteSession={sessId =>
                                                handleDeleteSession(sessId)
                                              }
                                              isDeletingSubject={deleteSubjectMutation.isPending}
                                              isDeletingSession={deleteSessionMutation.isPending}
                                            />
                                          ))
                                        ) : (
                                          <div className="p-6 bg-surface-container-lowest rounded-2xl text-center space-y-2 border border-dashed border-outline-variant/20">
                                            <p className="text-xs text-on-surface-variant/70 font-medium">
                                              <Bilingual {...copy.learningPathStudio.noSubjects} />
                                            </p>
                                            <Button
                                              size="sm"
                                              onClick={() => {
                                                setSelectedSubjectStageId(stage.id);
                                                setSelectedSubjectStageName(stage.name);
                                                setEditingSubject(undefined);
                                                setIsSubjectModalOpen(true);
                                              }}
                                              className="rounded-xl px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider"
                                            >
                                              <Plus size={13} className="mr-1" />
                                              <Bilingual {...copy.learningPathStudio.addSubject} />
                                            </Button>
                                          </div>
                                        )}

                                        {/* Stage-level Evaluations / Exams at End of Stage */}
                                        <div className="pt-4 border-t border-outline-variant/10 space-y-3">
                                          {stage.evaluations && stage.evaluations.length > 0 && (
                                            <div className="space-y-2">
                                              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-secondary">
                                                <Bilingual
                                                  id="Evaluasi / Ujian Akhir Tahap"
                                                  ar="تقييم / امتحان نهاية المرحلة"
                                                />
                                              </span>
                                              {stage.evaluations.map(e => (
                                                <div
                                                  key={e.id}
                                                  className="flex items-center justify-between p-3.5 bg-surface-container-lowest rounded-2xl border border-secondary/20 hover:border-secondary/50 transition-all cursor-pointer group/eval"
                                                  onClick={() =>
                                                    void navigate(`/evaluations/${e.id}`)
                                                  }
                                                >
                                                  <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center">
                                                      <HelpCircle size={16} />
                                                    </div>
                                                    <div>
                                                      <span className="text-xs font-bold text-on-surface">
                                                        {e.title}
                                                      </span>
                                                      <p className="text-[9px] text-secondary font-black uppercase tracking-wider mt-0.5">
                                                        KKM:{' '}
                                                        {e.passingScore ?? stage.passingScore ?? 70}
                                                        % • {e.type}
                                                      </p>
                                                    </div>
                                                  </div>
                                                  <ExternalLink
                                                    size={14}
                                                    className="text-on-surface-variant opacity-0 group-hover/eval:opacity-100 transition-opacity"
                                                  />
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          <button
                                            type="button"
                                            onClick={() =>
                                              void navigate('/evaluations/create', {
                                                state: {
                                                  programId: id,
                                                  levelId: level.id,
                                                  stageId: stage.id,
                                                  passingScore: stage.passingScore ?? 70,
                                                },
                                              })
                                            }
                                            className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-secondary/30 rounded-2xl text-secondary hover:border-secondary hover:bg-secondary/5 transition-all text-[10px] font-bold uppercase tracking-wider"
                                          >
                                            <Plus size={14} />
                                            <Bilingual
                                              id="Jadwalkan Evaluasi / Ujian Akhir Tahap (KKM:"
                                              ar="جدولة تقييم نهاية المرحلة (KKM:"
                                            />{' '}
                                            {stage.passingScore ?? 70}%)
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <div className="p-8 bg-surface-container-low/40 rounded-3xl border-2 border-dashed border-outline-variant/20 text-center space-y-3">
                                <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto">
                                  <FolderPlus size={24} />
                                </div>
                                <div>
                                  <h5 className="font-headline font-bold text-on-surface text-sm">
                                    <Bilingual {...copy.learningPathStudio.noStages} />
                                  </h5>
                                  <p className="text-xs text-on-surface-variant/70 mt-1 max-w-md mx-auto">
                                    Buat tahapan/tema pembelajaran baru (contoh: Tahap A - Adab &
                                    Manhaj) untuk mulai menyusun mata pelajaran dan pertemuan.
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedStageLevelId(level.id);
                                    setSelectedStageLevelName(level.name);
                                    setEditingStage(undefined);
                                    setIsStageModalOpen(true);
                                  }}
                                  className="rounded-xl px-5 py-2 font-bold text-xs shadow-level2"
                                >
                                  <Plus size={15} className="mr-1.5" />
                                  <Bilingual {...copy.learningPathStudio.addStage} />
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-12 bg-surface-container-low/30 rounded-[2rem] border-2 border-dashed border-outline-variant/20 text-center space-y-4">
                    <div className="w-16 h-16 rounded-3xl bg-primary/10 text-primary flex items-center justify-center mx-auto shadow-level1">
                      <Layers size={32} />
                    </div>
                    <div>
                      <h4 className="text-lg font-headline font-bold text-on-surface">
                        <Bilingual {...copy.learningPathStudio.noLevels} />
                      </h4>
                      <p className="text-xs text-on-surface-variant/70 mt-1 max-w-lg mx-auto">
                        Mulai bangun kurikulum 6 tingkat dengan menambahkan Level pertama (contoh:
                        Level 1 - Dasar).
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setEditingLevel(undefined);
                        setIsLevelModalOpen(true);
                      }}
                      className="rounded-xl px-6 py-3 font-bold text-xs shadow-level2"
                    >
                      <Plus size={16} className="mr-2" />
                      <Bilingual {...copy.learningPathStudio.addLevel} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Rail */}
          <aside className="min-w-0 space-y-10 xl:sticky xl:top-6">
            <Card className="border border-on-surface/5">
              <div className="flex items-center justify-between mb-8">
                <h4 className="text-[10px] font-label font-black text-on-surface-variant uppercase tracking-[0.2em] m-0">
                  <Bilingual {...copy.programDetail.institutionalImpact} />
                </h4>
                <BarChart3 size={18} className="text-primary/40" />
              </div>
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-[11px] font-bold text-on-surface">
                    <span>
                      <Bilingual {...copy.programDetail.knowledgeVelocity} />
                    </span>
                    <span className="text-primary">{averageProgress}%</span>
                  </div>
                  <div className="h-1 bg-surface-container-high rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${averageProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </aside>
        </div>
      ) : activeTab === 'WHATSAPP' ? (
        <div className="space-y-10 animate-fadeIn">
          {/* Section 1: Form & Registration Settings for this Program */}
          <div className="bg-surface-container-lowest p-6 sm:p-8 rounded-3xl shadow-level1 border border-on-surface/5 space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-outline-variant/10 pb-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-primary font-bold text-[10px] uppercase tracking-widest font-label">
                  <Sparkles size={14} className="text-secondary" />
                  <span>Pengaturan Form & Status Pendaftaran</span>
                </div>
                <h3 className="text-xl font-headline font-bold text-on-surface m-0">
                  Konfigurasi Pendaftaran Program ({program.code})
                </h3>
                <p className="text-xs font-body text-on-surface-variant max-w-2xl opacity-70">
                  Atur apakah pendaftaran dibuka untuk umum, tentukan seleksi berkas otomatis atau
                  manual, dan bagikan tautan pendaftaran publik kepada calon peserta.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setIsProgramModalOpen(true)}
                  className="px-4 py-2 text-xs font-label font-bold uppercase tracking-widest rounded-xl border border-outline-variant/20"
                >
                  <Pencil size={14} />
                  <span>Edit Info Lengkap</span>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Registration Open/Close Switch */}
              <div className="p-5 rounded-2xl bg-surface-container-low/40 border border-on-surface/5 flex flex-col justify-between">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-60">
                    Status Pendaftaran
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${
                        program.isRegistrationOpen !== false
                          ? 'bg-primary animate-pulse'
                          : 'bg-outline-variant'
                      }`}
                    />
                    <h4 className="text-sm font-headline font-bold text-on-surface m-0">
                      {program.isRegistrationOpen !== false
                        ? 'Pendaftaran DIBUKA'
                        : 'Pendaftaran DITUTUP'}
                    </h4>
                  </div>
                  <p className="text-[11px] font-body text-on-surface-variant opacity-70">
                    {program.isRegistrationOpen !== false
                      ? 'Calon peserta dapat melihat dan mendaftar ke program ini.'
                      : 'Formulir pendaftaran ditutup untuk umum.'}
                  </p>
                </div>

                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      const nextState = program.isRegistrationOpen === false;
                      updateProgramMutation.mutate({
                        id: program.id,
                        data: { isRegistrationOpen: nextState },
                      });
                      addNotification({
                        type: 'success',
                        title: 'Status Pendaftaran',
                        message: nextState
                          ? 'Pendaftaran program berhasil DIBUKA'
                          : 'Pendaftaran program berhasil DITUTUP',
                      });
                    }}
                    className={`w-full py-2.5 px-4 rounded-xl text-xs font-label font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-xs ${
                      program.isRegistrationOpen !== false
                        ? 'bg-primary text-on-primary hover:bg-primary/90'
                        : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'
                    }`}
                  >
                    <Globe size={14} />
                    <span>
                      {program.isRegistrationOpen !== false
                        ? 'Tutup Pendaftaran'
                        : 'Buka Pendaftaran'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Approval Mode */}
              <div className="p-5 rounded-2xl bg-surface-container-low/40 border border-on-surface/5 flex flex-col justify-between">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-60">
                    Mode Persetujuan (Approval)
                  </span>
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={16} className="text-primary" />
                    <h4 className="text-sm font-headline font-bold text-on-surface m-0">
                      {program.approvalMode === 'AUTO_APPROVE'
                        ? 'Persetujuan Otomatis (Auto)'
                        : 'Persetujuan Manual (Review)'}
                    </h4>
                  </div>
                  <p className="text-[11px] font-body text-on-surface-variant opacity-70">
                    {program.approvalMode === 'AUTO_APPROVE'
                      ? 'Peserta yang mengisi form langsung diterima otomatis.'
                      : 'Admin perlu meninjau berkas pendaftar sebelum disetujui.'}
                  </p>
                </div>

                <div className="pt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      updateProgramMutation.mutate({
                        id: program.id,
                        data: { approvalMode: 'MANUAL' },
                      });
                      addNotification({
                        type: 'success',
                        title: 'Mode Persetujuan',
                        message: 'Mode persetujuan diubah ke Seleksi Manual',
                      });
                    }}
                    className={`flex-1 py-2 px-3 rounded-xl text-[10px] font-label font-bold uppercase tracking-wider transition-all ${
                      program.approvalMode !== 'AUTO_APPROVE'
                        ? 'bg-primary text-on-primary shadow-xs'
                        : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
                    }`}
                  >
                    Manual
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      updateProgramMutation.mutate({
                        id: program.id,
                        data: { approvalMode: 'AUTO_APPROVE' },
                      });
                      addNotification({
                        type: 'success',
                        title: 'Mode Persetujuan',
                        message: 'Mode persetujuan diubah ke Otomatis (Auto-Approve)',
                      });
                    }}
                    className={`flex-1 py-2 px-3 rounded-xl text-[10px] font-label font-bold uppercase tracking-wider transition-all ${
                      program.approvalMode === 'AUTO_APPROVE'
                        ? 'bg-primary text-on-primary shadow-xs'
                        : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
                    }`}
                  >
                    Otomatis
                  </button>
                </div>
              </div>

              {/* Optional institution-wide participant NIP */}
              <div className="flex flex-col justify-between rounded-2xl bg-secondary-container/35 p-5">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-60">
                    <Bilingual {...copy.registrations.nipSettingLabel} />
                  </span>
                  <div className="flex items-center gap-2">
                    <Fingerprint size={16} className="text-primary" aria-hidden="true" />
                    <h4 className="m-0 text-sm font-headline font-bold text-on-surface">
                      <Bilingual
                        {...(program.generateParticipantNip
                          ? copy.registrations.nipAutomatic
                          : copy.registrations.nipOptionalDisabled)}
                      />
                    </h4>
                  </div>
                  <p className="text-[11px] font-body leading-5 text-on-surface-variant opacity-80">
                    <Bilingual {...copy.registrations.nipSettingDescription} />
                  </p>
                  <p className="font-mono text-[10px] font-bold leading-5 text-primary">
                    <Bilingual {...copy.registrations.nipFormatShort} />
                  </p>
                </div>
                <button
                  type="button"
                  disabled={updateProgramMutation.isPending}
                  onClick={() => {
                    const nextState = !program.generateParticipantNip;
                    updateProgramMutation.mutate(
                      { id: program.id, data: { generateParticipantNip: nextState } },
                      {
                        onSuccess: () =>
                          addNotification({
                            type: 'success',
                            title: t(copy.registrations.nipSettingLabel),
                            message: t(
                              nextState
                                ? copy.registrations.nipEnabledSuccess
                                : copy.registrations.nipDisabledSuccess
                            ),
                          }),
                      }
                    );
                  }}
                  aria-label={t(copy.registrations.nipSettingLabel)}
                  aria-pressed={program.generateParticipantNip}
                  className={`mt-4 flex min-h-10 w-full items-center justify-center rounded-xl px-4 text-xs font-label font-bold uppercase tracking-wider transition-colors disabled:cursor-wait disabled:opacity-60 ${program.generateParticipantNip ? 'bg-primary text-on-primary' : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'}`}
                >
                  <Bilingual
                    {...(program.generateParticipantNip
                      ? copy.registrations.disableNip
                      : copy.registrations.enableNip)}
                  />
                </button>
              </div>

              {/* Public Registration Link */}
              <div className="p-5 rounded-2xl bg-primary/5 border border-primary/10 flex flex-col justify-between">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-label font-bold uppercase tracking-widest text-primary font-mono">
                    Tautan Form Publik Peserta
                  </span>
                  <div className="bg-surface-container-lowest p-2.5 rounded-xl border border-primary/20 break-all text-xs font-mono text-on-surface">
                    https://my.tsl.my.id/daftar/{program.code}
                  </div>
                  <p className="text-[10px] font-body text-on-surface-variant opacity-70">
                    Bagikan tautan ini kepada calon peserta agar langsung terarah ke form program
                    ini.
                  </p>
                </div>

                <div className="pt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          `https://my.tsl.my.id/daftar/${encodeURIComponent(program.code)}`
                        );
                        setCopiedLink(true);
                        addNotification({
                          type: 'success',
                          title: 'Tautan Disalin',
                          message: 'Link pendaftaran peserta berhasil disalin.',
                        });
                        setTimeout(() => setCopiedLink(false), 2500);
                      } catch {
                        // ignore
                      }
                    }}
                    className="flex-1 py-2.5 px-3 rounded-xl bg-primary text-on-primary text-xs font-label font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-xs hover:bg-primary/90 transition-all"
                  >
                    {copiedLink ? <Check size={14} /> : <Copy size={14} />}
                    <span>{copiedLink ? 'Tersalin!' : 'Salin Tautan'}</span>
                  </button>
                  <a
                    href={`https://my.tsl.my.id/daftar/${encodeURIComponent(program.code)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2.5 rounded-xl bg-surface-container-highest text-on-surface hover:bg-surface-container-low transition-colors flex items-center justify-center"
                    title="Buka Formulir Pendaftaran"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: WhatsApp & Telegram Group Channels */}
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-0">
              <div>
                <h2 className="text-3xl font-headline font-bold text-on-surface m-0 tracking-tighter">
                  <Bilingual {...copy.programDetail.communicationChannels} />
                </h2>
                <p className="text-sm font-body text-on-surface-variant mt-1 opacity-60">
                  <Bilingual {...copy.programDetail.communicationChannelsDesc} />
                </p>
              </div>
              <Button
                onClick={() => {
                  setEditingWhatsAppGroup(undefined);
                  setIsWhatsAppModalOpen(true);
                }}
                size="sm"
                className="gap-2 rounded-xl text-[10px] uppercase font-bold tracking-widest px-6 shadow-level1 w-full md:w-auto justify-center md:justify-start"
              >
                <Plus size={14} /> <Bilingual {...copy.programDetail.btnNewChannel} />
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(whatsappGroups as WhatsAppGroup[] | undefined)?.map(group => (
                <div
                  key={group.id}
                  className="relative border border-on-surface/5 bg-surface-container-lowest rounded-[1.5rem] p-6 shadow-level1 hover:shadow-level2 transition-all duration-200 flex flex-col"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-3 rounded-2xl bg-secondary/10 text-secondary">
                      <MessageSquare size={18} />
                    </div>
                    <div className="flex gap-2">
                      <span
                        className={`text-[9px] font-label font-black uppercase tracking-[0.2em] px-2 py-1 rounded ${
                          group.gender === 'IKHWAN'
                            ? 'bg-primary/10 text-primary'
                            : group.gender === 'AKHWAT'
                              ? 'bg-tertiary/10 text-tertiary'
                              : 'bg-secondary/10 text-secondary'
                        }`}
                      >
                        {group.gender}
                      </span>
                    </div>
                  </div>
                  <h4 className="text-sm font-headline font-bold text-on-surface m-0 line-clamp-1">
                    {group.name}
                  </h4>
                  <p className="text-[10px] text-on-surface-variant/60 font-medium mt-1 truncate">
                    {group.inviteLink || t(copy.programDetail.noInviteLink)}
                  </p>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2 text-[10px] font-label font-bold text-on-surface-variant opacity-60">
                      <Users size={12} />
                      <span>
                        {group.maxMembers || 1000}{' '}
                        <Bilingual {...copy.programDetail.maxMembersSuffix} />
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-label font-bold text-on-surface-variant opacity-60">
                      <Clock size={12} />
                      <span>
                        <Bilingual {...copy.programDetail.ageLabel} /> {group.ageMin ?? 0}-
                        {group.ageMax ?? '∞'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-outline-variant/10 flex gap-3">
                    <Link
                      to={`/whatsapp-groups/${group.id}`}
                      className="flex-1 bg-primary/5 hover:bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest py-2 rounded-lg text-center transition-colors no-underline flex items-center justify-center gap-2"
                    >
                      <Search size={12} />
                      <Bilingual {...copy.programDetail.btnInspect} />
                    </Link>
                    <button
                      onClick={() => {
                        setEditingWhatsAppGroup(group);
                        setIsWhatsAppModalOpen(true);
                      }}
                      className="flex-1 bg-secondary/5 hover:bg-secondary/10 text-secondary text-[10px] font-black uppercase tracking-widest py-2 rounded-lg text-center transition-colors flex items-center justify-center gap-2"
                    >
                      <Bookmark size={12} />
                      <Bilingual {...copy.programDetail.btnEditSpec} />
                    </button>
                  </div>
                </div>
              ))}
              {(!whatsappGroups || (whatsappGroups as WhatsAppGroup[]).length === 0) && (
                <div className="col-span-full py-20 text-center bg-surface-container-low/20 rounded-[3rem] border-2 border-dashed border-outline-variant/10 italic text-on-surface-variant opacity-40 font-body">
                  <Bilingual {...copy.programDetail.noChannels} />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'OVERVIEW' ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 animate-fadeIn">
          <div className="lg:col-span-9 space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-0">
              <div className="flex overflow-x-auto w-full md:w-auto gap-4 pb-2 md:pb-0 scrollbar-hide">
                <button
                  onClick={() => setParticipantsSubTab('ACTIVE')}
                  className={`text-xs font-bold uppercase tracking-widest px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                    participantsSubTab === 'ACTIVE'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  <Bilingual {...copy.programDetail.statusActive} />
                </button>
                <button
                  onClick={() => setParticipantsSubTab('PENDING')}
                  className={`text-xs font-bold uppercase tracking-widest px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                    participantsSubTab === 'PENDING'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  <Bilingual {...copy.programDetail.statusPending} />
                </button>
              </div>
              <Button
                onClick={() => setIsManualEnrollModalOpen(true)}
                size="sm"
                className="gap-2 rounded-xl text-[10px] uppercase font-bold tracking-widest px-6 shadow-level1 w-full md:w-auto justify-center md:justify-start"
              >
                <Plus size={14} /> <Bilingual {...copy.programDetail.btnManualEnroll} />
              </Button>
            </div>

            <Card className="border border-on-surface/5 p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] text-left border-collapse">
                  <thead className="bg-surface-container-low/50">
                    <tr>
                      <th className="px-6 py-4 text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                        Identitas Peserta
                      </th>
                      <th className="px-6 py-4 text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                        Status
                      </th>
                      <th className="px-6 py-4 text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                        Progress / Catatan
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-on-surface/5">
                    {participantsSubTab === 'ACTIVE' &&
                      (isLoadingEnrollments ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-6 py-12 text-center text-on-surface-variant opacity-60"
                          >
                            <LoadingSpinner />
                          </td>
                        </tr>
                      ) : activeEnrollments?.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-6 py-12 text-center text-on-surface-variant opacity-60 text-sm"
                          >
                            Belum ada peserta aktif
                          </td>
                        </tr>
                      ) : (
                        activeEnrollments?.map(enroll => (
                          <tr
                            key={enroll.id}
                            onClick={() => {
                              if (enroll.userId) setDetailUserId(enroll.userId);
                            }}
                            className="hover:bg-surface-container-lowest/50 transition-colors cursor-pointer"
                          >
                            <td className="px-6 py-4">
                              <p className="font-bold text-sm text-on-surface">
                                {enroll.user?.name}
                              </p>
                              <p className="text-xs text-on-surface-variant">
                                {enroll.user?.email}
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider rounded">
                                {enroll.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-16 bg-surface-container-high rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full opacity-60"
                                    style={{ width: `${enroll.progress}%` }}
                                  />
                                </div>
                                <span className="text-[10px] font-mono font-bold text-primary">
                                  {enroll.progress}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))
                      ))}

                    {participantsSubTab === 'PENDING' &&
                      (isLoadingPending ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-6 py-12 text-center text-on-surface-variant opacity-60"
                          >
                            <LoadingSpinner />
                          </td>
                        </tr>
                      ) : pendingRegistrationsData?.items?.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-6 py-12 text-center text-on-surface-variant opacity-60 text-sm"
                          >
                            Tidak ada pendaftaran menunggu
                          </td>
                        </tr>
                      ) : (
                        pendingRegistrationsData?.items?.map(reg => (
                          <tr
                            key={reg.id}
                            onClick={() => {
                              if (reg.userId) setDetailUserId(reg.userId);
                            }}
                            className="hover:bg-surface-container-lowest/50 transition-colors cursor-pointer"
                          >
                            <td className="px-6 py-4">
                              <p className="font-bold text-sm text-on-surface">{reg.user?.name}</p>
                              <p className="text-xs text-on-surface-variant">{reg.user?.email}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 bg-tertiary/10 text-tertiary text-[10px] font-bold uppercase tracking-wider rounded">
                                {reg.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-xs text-on-surface-variant">
                              {new Date(reg.registeredAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      ))}
                  </tbody>
                </table>
              </div>
            </Card>
            {participantsSubTab === 'ACTIVE' && activeEnrollmentsData?.meta && (
              <Pagination
                currentPage={activeEnrollmentsData.meta.page}
                totalPages={activeEnrollmentsData.meta.totalPages}
                onPageChange={setParticipantPage}
              />
            )}
          </div>

          <div className="lg:col-span-3 space-y-6">
            <div className="p-6 bg-surface-container-low rounded-[2rem] border border-on-surface/5 flex flex-col items-center text-center">
              <span className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] mb-4">
                <Bilingual {...copy.programDetail.participantBase} />
              </span>
              <p className="text-5xl font-headline font-black text-on-surface m-0">
                {program._count?.enrollments || 0}
              </p>
              <p className="text-xs font-body text-on-surface-variant mt-3 opacity-60 leading-relaxed">
                <Bilingual {...copy.programDetail.participantBaseDesc} />
              </p>
            </div>

            <Card className="border border-on-surface/5 p-6">
              <h3 className="text-sm font-headline font-bold text-on-surface mb-6 flex items-center gap-2">
                <Bookmark className="text-primary" size={18} />
                <Bilingual {...copy.programDetail.operationalConfig} />
              </h3>
              <div className="space-y-6">
                {[
                  { label: copy.programDetail.metaEntityKey, value: program.code },
                  { label: copy.programDetail.metaAccessControl, value: program.approvalMode },
                  {
                    label: copy.programDetail.metaRegStatus,
                    value: t(
                      program.isRegistrationOpen
                        ? copy.programDetail.statusOpen
                        : copy.programDetail.statusClosed
                    ),
                  },
                ].map(meta => (
                  <div key={meta.label.id} className="space-y-1">
                    <p className="text-[9px] font-label font-bold text-on-surface-variant uppercase tracking-[0.2em] opacity-60">
                      <Bilingual {...meta.label} />
                    </p>
                    <p className="text-sm font-body font-bold text-on-surface m-0">{meta.value}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      ) : activeTab === 'MATERIALS' ? (
        <div className="space-y-10 animate-fadeIn">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-headline font-bold text-on-surface m-0 tracking-tighter">
                <Bilingual {...copy.programDetail.assetRepository} />
              </h2>
              <p className="text-sm font-body text-on-surface-variant mt-1 opacity-60">
                <Bilingual {...copy.programDetail.assetRepositoryDesc} />
              </p>
            </div>
            <div className="flex gap-4">
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant size-4 opacity-40 group-focus-within:text-primary transition-all" />
                <input
                  className="bg-surface-container-low border-none rounded-xl pl-10 pr-4 py-2.5 text-xs font-body w-64 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  placeholder={t(copy.programDetail.filterRepository)}
                  type="text"
                  value={searchMaterialQuery}
                  onChange={e => setSearchMaterialQuery(e.target.value)}
                />
              </div>
              <Button
                onClick={() => {
                  setActiveTab('CURRICULUM');
                  addNotification({
                    type: 'info',
                    title: t(copy.programDetail.notifModularInjectionTitle),
                    message: t(copy.programDetail.notifModularInjectionMsg),
                  });
                }}
                size="sm"
                className="gap-2 rounded-xl text-[10px] uppercase font-bold tracking-widest px-6 shadow-level1"
              >
                <Plus size={14} /> <Bilingual {...copy.programDetail.btnInjectAsset} />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMaterials.map(material => (
              <div
                key={material.id}
                onClick={() => {
                  setEditingMaterial(material);
                  setSelectedFlowId(material.flowId || '');
                  setIsMaterialModalOpen(true);
                }}
              >
                <Card className="relative group hover:-translate-y-1 border border-on-surface/5 cursor-pointer h-full">
                  <div className="flex justify-between items-start mb-6">
                    <div
                      className={`p-3 rounded-2xl ${
                        material.type === 'VIDEO'
                          ? 'bg-primary/5 text-primary'
                          : material.type === 'QUIZ'
                            ? 'bg-secondary/5 text-secondary'
                            : 'bg-tertiary/5 text-tertiary'
                      }`}
                    >
                      {getMaterialIcon(material.type)}
                    </div>
                    <button className="p-1.5 opacity-0 group-hover:opacity-40 transition-opacity hover:opacity-100">
                      <MoreVertical size={16} />
                    </button>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[9px] font-label font-black text-primary uppercase tracking-[0.2em]">
                      {material.type}
                    </span>
                    <h4 className="text-sm font-headline font-bold text-on-surface m-0 line-clamp-1">
                      {material.title}
                    </h4>
                  </div>
                  <div className="mt-8 pt-4 border-t border-outline-variant/10 flex justify-between items-center">
                    <div className="flex items-center gap-2 text-[10px] font-label font-bold text-on-surface-variant opacity-40">
                      <Clock size={12} />
                      <span>
                        <Bilingual {...copy.programDetail.activeAsset} />
                      </span>
                    </div>
                    <button className="text-primary hover:underline transition-all">
                      <ExternalLink size={14} />
                    </button>
                  </div>
                </Card>
              </div>
            ))}
            {filteredMaterials.length === 0 && (
              <div className="col-span-full py-32 text-center bg-surface-container-low/20 rounded-[3rem] border-2 border-dashed border-outline-variant/10 italic text-on-surface-variant opacity-40 font-body">
                {searchMaterialQuery ? (
                  <span>Tidak ada aset yang cocok dengan pencarian Anda</span>
                ) : (
                  <Bilingual {...copy.programDetail.repositoryEmpty} />
                )}
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'EVALUATIONS' ? (
        <div className="space-y-10 animate-fadeIn">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-0">
            <div>
              <h2 className="text-3xl font-headline font-bold text-on-surface m-0 tracking-tighter">
                <Bilingual {...copy.programDetail.assessmentMatrix} />
              </h2>
              <p className="text-sm font-body text-on-surface-variant mt-1 opacity-60">
                <Bilingual {...copy.programDetail.assessmentMatrixDesc} />
              </p>
            </div>
            <Button
              onClick={() => void navigate('/evaluations/create', { state: { programId: id } })}
              size="sm"
              className="gap-2 rounded-xl text-[10px] uppercase font-bold tracking-widest px-6 shadow-level1 w-full md:w-auto justify-center md:justify-start"
            >
              <Plus size={14} /> <Bilingual {...copy.programDetail.btnNewValidationNode} />
            </Button>
          </div>

          <div className="bg-surface-container-lowest rounded-2xl overflow-hidden shadow-level1 border border-on-surface/5 overflow-x-auto">
            <div className="min-w-[800px]">
              <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_60px] gap-6 px-8 py-5 bg-surface-container-low/50 border-b border-on-surface/5">
                {[
                  copy.programDetail.headerEvaluationNode,
                  copy.programDetail.headerContext,
                  copy.programDetail.headerAttemptLimit,
                  copy.programDetail.headerBenchmarking,
                  null,
                ].map((h, i) => (
                  <div
                    key={i}
                    className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60"
                  >
                    {h ? <Bilingual {...h} /> : null}
                  </div>
                ))}
              </div>
              <div className="divide-y divide-on-surface/5">
                {[
                  ...(program.evaluations?.map(e => ({
                    ...e,
                    _context: 'Program' as const,
                  })) || []),
                  ...(program.levels?.flatMap(
                    l =>
                      l.evaluations?.map(e => ({
                        ...e,
                        _context: 'Phase' as const,
                        _contextLabel: `Phase ${l.order}: ${l.name}`,
                      })) || []
                  ) || []),
                  ...(program.levels?.flatMap(
                    l =>
                      l.flows?.flatMap(
                        f =>
                          f.evaluations?.map(e => ({
                            ...e,
                            _context: 'Flow' as const,
                            _contextLabel: `Flow: ${f.name} (Phase ${l.order})`,
                          })) || []
                      ) || []
                  ) || []),
                  ...(program.levels?.flatMap(
                    l =>
                      l.flows?.flatMap(
                        f =>
                          f.materials?.flatMap(
                            m =>
                              m.evaluations?.map(e => ({
                                ...e,
                                _context: 'Material' as const,
                                _contextLabel: `Material: ${m.title}`,
                              })) || []
                          ) || []
                      ) || []
                  ) || []),
                  ...(program.levels?.flatMap(
                    level =>
                      level.stages?.flatMap(
                        stage =>
                          stage.subjects?.flatMap(
                            subject =>
                              subject.evaluations?.map(e => ({
                                ...e,
                                _context: 'Subject' as const,
                                _contextLabel: `Mata Pelajaran: ${subject.name}`,
                              })) || []
                          ) || []
                      ) || []
                  ) || []),
                  ...(program.levels?.flatMap(
                    level =>
                      level.stages?.flatMap(
                        stage =>
                          stage.subjects?.flatMap(
                            subject =>
                              subject.sessions?.flatMap(
                                session =>
                                  session.evaluations?.map(e => ({
                                    ...e,
                                    _context: 'Session' as const,
                                    _contextLabel: `Pertemuan ${session.sessionNumber}: ${session.title} • ${subject.name}`,
                                  })) || []
                              ) || []
                          ) || []
                      ) || []
                  ) || []),
                ]
                  .filter((v, i, a) => a.findIndex(t => t.id === v.id) === i)
                  .map(
                    (
                      evaluation: EvaluationWithMaterial & {
                        _context?: string;
                        _contextLabel?: string;
                      }
                    ) => {
                      const attempts =
                        (evaluation as { attempts?: { score: number | null }[] }).attempts || [];
                      const validScores = attempts
                        .map((a: { score: number | null }) => a.score)
                        .filter((s: number | null): s is number => s !== null && s !== undefined);
                      const avgScore =
                        validScores.length > 0
                          ? Math.round(
                              validScores.reduce((acc: number, curr: number) => acc + curr, 0) /
                                validScores.length
                            )
                          : 0;
                      const avgScoreText = t(copy.programDetail.avgSuffix).replace(
                        '--%',
                        avgScore > 0 ? `${avgScore}%` : '--%'
                      );

                      return (
                        <div
                          key={evaluation.id}
                          className="grid grid-cols-[1.5fr_1fr_1fr_1fr_60px] gap-6 px-8 py-6 hover:bg-surface-container-low/30 transition-colors duration-150 items-center group"
                        >
                          <div className="space-y-1">
                            <div className="text-sm font-body font-bold text-on-surface group-hover:text-primary transition-colors">
                              {evaluation.title}
                            </div>
                            <div className="text-[10px] text-on-surface-variant/60 font-medium uppercase tracking-wider flex items-center gap-1.5">
                              {evaluation._context === 'Material' && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-secondary/40" />
                                  <Bilingual {...copy.programDetail.contextMaterial} />:{' '}
                                  {(
                                    evaluation as { _contextLabel?: string }
                                  )._contextLabel?.replace('Material: ', '')}
                                </>
                              )}
                              {evaluation._context === 'Flow' && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-tertiary/40" />
                                  <span className="text-tertiary">
                                    {(evaluation as { _contextLabel?: string })._contextLabel}
                                  </span>
                                </>
                              )}
                              {evaluation._context === 'Phase' && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-secondary/40" />
                                  <span className="text-secondary">
                                    {(evaluation as { _contextLabel?: string })._contextLabel}
                                  </span>
                                </>
                              )}
                              {evaluation._context === 'Program' && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-primary/40" />
                                  <Bilingual {...copy.programDetail.programLevelEval} />
                                </>
                              )}
                              {evaluation._context === 'Session' && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-secondary/40" />
                                  <span className="text-secondary">
                                    {(evaluation as { _contextLabel?: string })._contextLabel}
                                  </span>
                                </>
                              )}
                              {evaluation._context === 'Subject' && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-primary/40" />
                                  <span className="text-primary">
                                    {(evaluation as { _contextLabel?: string })._contextLabel}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-[10px] font-mono font-bold text-on-surface-variant bg-surface-container-high px-2 py-1 rounded w-fit uppercase">
                            {evaluation.type}
                          </div>
                          <div className="text-xs font-body text-on-surface-variant">
                            {evaluation.maxAttempts}{' '}
                            <Bilingual {...copy.programDetail.attemptsAllowed} />
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-16 bg-surface-container-high rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full opacity-60 transition-all duration-500 ease-out"
                                style={{ width: `${avgScore}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-mono font-bold text-primary">
                              {avgScoreText}
                            </span>
                          </div>
                          <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => void navigate(`/evaluations/${evaluation.id}`)}
                              className="p-2 text-on-surface-variant hover:text-primary transition-all"
                            >
                              <ChevronRight size={18} />
                            </button>
                          </div>
                        </div>
                      );
                    }
                  )}
                {!program.evaluations?.length &&
                  !program.levels?.some(level =>
                    level.stages?.some(stage =>
                      stage.subjects?.some(subject => subject.evaluations?.length)
                    )
                  ) &&
                  !program.levels?.some(level =>
                    level.stages?.some(stage =>
                      stage.subjects?.some(subject =>
                        subject.sessions?.some(session => session.evaluations?.length)
                      )
                    )
                  ) &&
                  !program.levels?.some(l =>
                    l.flows?.some(f => f.materials?.some(m => m.evaluations?.length))
                  ) && (
                    <div className="px-8 py-20 text-center bg-surface-container-low/20 italic text-on-surface-variant text-sm font-body">
                      <Bilingual {...copy.programDetail.noValidationNodes} />
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isMaterialModalOpen && (
        <MaterialModal
          material={editingMaterial}
          flowId={selectedFlowId}
          programId={id || ''}
          onClose={() => setIsMaterialModalOpen(false)}
        />
      )}

      {isImportMaterialOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-surface/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-surface-container-lowest w-full max-w-2xl rounded-3xl shadow-level4 border border-on-surface/5 overflow-hidden flex flex-col max-h-[90vh] animate-scaleIn">
            <div className="p-8 border-b border-on-surface/5 bg-surface-container-low/30">
              <h2 className="text-2xl font-headline font-black text-primary tracking-tight uppercase m-0 leading-none">
                <Bilingual {...copy.programDetail.importTitle} />
              </h2>
              <p className="text-sm font-body text-on-surface-variant mt-2">
                <Bilingual {...copy.programDetail.importDesc} />
              </p>
            </div>

            <div className="p-8 overflow-y-auto space-y-6 flex-1">
              {/* Program Selection */}
              <div className="space-y-2">
                <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                  <Bilingual {...copy.programDetail.importLabelSourceProgram} />
                </label>
                <select
                  className="w-full bg-surface-container border-none rounded-xl px-4 py-3 font-body text-sm outline-none"
                  value={selectedSourceProgramId}
                  onChange={e => {
                    setSelectedSourceProgramId(e.target.value);
                    setSelectedSourceFlowId('');
                    setSelectedSourceMaterialId('');
                  }}
                >
                  <option value="">{t(copy.programDetail.importPlaceholderProgram)}</option>
                  {allProgramsData?.items?.map(prog => (
                    <option key={prog.id} value={prog.id}>
                      {prog.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Learning Flow Selection */}
              {selectedSourceProgramId && sourceProgramDetail && (
                <div className="space-y-2">
                  <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                    <Bilingual {...copy.programDetail.importLabelSourceFlow} />
                  </label>
                  <select
                    className="w-full bg-surface-container border-none rounded-xl px-4 py-3 font-body text-sm outline-none"
                    value={selectedSourceFlowId}
                    onChange={e => {
                      setSelectedSourceFlowId(e.target.value);
                      setSelectedSourceMaterialId('');
                    }}
                  >
                    <option value="">{t(copy.programDetail.importPlaceholderFlow)}</option>
                    {sourceProgramDetail.levels
                      ?.flatMap(l => l.flows || [])
                      ?.map(flow => (
                        <option key={flow.id} value={flow.id}>
                          {flow.name} ({flow.materials?.length ?? 0}{' '}
                          {t(copy.programDetail.importAssetsSuffix)})
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Material Selection */}
              {selectedSourceFlowId && sourceProgramDetail && (
                <div className="space-y-2">
                  <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                    <Bilingual {...copy.programDetail.importLabelSelectAsset} />
                  </label>
                  <select
                    className="w-full bg-surface-container border-none rounded-xl px-4 py-3 font-body text-sm outline-none"
                    value={selectedSourceMaterialId}
                    onChange={e => setSelectedSourceMaterialId(e.target.value)}
                  >
                    <option value="">{t(copy.programDetail.importPlaceholderMaterial)}</option>
                    {sourceProgramDetail.levels
                      ?.flatMap(l => l.flows || [])
                      ?.find(f => f.id === selectedSourceFlowId)
                      ?.materials?.map(mat => (
                        <option key={mat.id} value={mat.id}>
                          {mat.title} ({mat.type})
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Asset Preview */}
              {selectedSourceMaterialId &&
                sourceProgramDetail &&
                (() => {
                  const selectedMaterial = sourceProgramDetail.levels
                    ?.flatMap(l => l.flows || [])
                    ?.find(f => f.id === selectedSourceFlowId)
                    ?.materials?.find(m => m.id === selectedSourceMaterialId);

                  if (!selectedMaterial) return null;

                  return (
                    <div className="space-y-3">
                      <h4 className="text-xs font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                        <Bilingual {...copy.programDetail.importBlueprint} />
                      </h4>
                      <div className="p-5 bg-surface-container/30 border border-on-surface/5 rounded-2xl space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-[9px] font-label font-black text-primary uppercase tracking-[0.2em]">
                              {selectedMaterial.type}
                            </span>
                            <h4 className="text-base font-headline font-bold text-on-surface m-0">
                              {selectedMaterial.title}
                            </h4>
                          </div>
                          <div className="p-2.5 bg-primary/10 text-primary rounded-xl">
                            {getMaterialIcon(selectedMaterial.type)}
                          </div>
                        </div>
                        {selectedMaterial.description && (
                          <p className="text-xs font-body text-on-surface-variant leading-relaxed opacity-70">
                            {selectedMaterial.description}
                          </p>
                        )}
                        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-on-surface/5">
                          <div>
                            <span className="text-[9px] font-label font-bold text-on-surface-variant uppercase tracking-widest block opacity-60">
                              <Bilingual {...copy.programDetail.importDuration} />
                            </span>
                            <span className="text-xs font-body font-bold text-on-surface">
                              {selectedMaterial.duration || 0}{' '}
                              <Bilingual {...copy.programDetail.importMinutes} />
                            </span>
                          </div>
                          <div>
                            <span className="text-[9px] font-label font-bold text-on-surface-variant uppercase tracking-widest block opacity-60">
                              <Bilingual {...copy.programDetail.importValidationMatrix} />
                            </span>
                            <span className="text-xs font-body font-bold text-on-surface">
                              {selectedMaterial.evaluations?.length
                                ? t(copy.programDetail.importValidationYes)
                                : t(copy.programDetail.importValidationNone)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </div>

            <div className="p-8 border-t border-on-surface/5 flex gap-4 bg-surface-container-low/30">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsImportMaterialOpen(false);
                  setSelectedSourceProgramId('');
                  setSelectedSourceFlowId('');
                  setSelectedSourceMaterialId('');
                }}
                className="flex-1 h-12 rounded-xl text-xs font-label font-bold uppercase tracking-widest"
              >
                <Bilingual {...copy.programDetail.btnCancel} />
              </Button>
              <Button
                disabled={!selectedSourceMaterialId}
                onClick={async () => {
                  try {
                    await importMaterialMutation.mutateAsync({
                      sourceMaterialId: selectedSourceMaterialId,
                      targetFlowId: importTargetFlowId,
                    });
                    addNotification({
                      type: 'success',
                      title: t(copy.programDetail.notifAssetClonedTitle),
                      message: t(copy.programDetail.notifAssetClonedMsg),
                    });
                    setIsImportMaterialOpen(false);
                    setSelectedSourceProgramId('');
                    setSelectedSourceFlowId('');
                    setSelectedSourceMaterialId('');
                  } catch (err) {
                    addNotification({
                      type: 'error',
                      title: t(copy.programDetail.notifCloneFailedTitle),
                      message:
                        err instanceof Error
                          ? err.message
                          : t(copy.programDetail.notifCloneFailedFallback),
                    });
                  }
                }}
                isLoading={importMaterialMutation.isPending}
                className="flex-1 h-12 rounded-xl text-xs font-label font-bold uppercase tracking-widest shadow-level2"
              >
                <Bilingual {...copy.programDetail.btnImportAssetSubmit} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLevelModalOpen && (
        <LevelModal
          programId={id || ''}
          level={editingLevel}
          order={editingLevel?.order || (program.levels?.length || 0) + 1}
          onClose={() => {
            setIsLevelModalOpen(false);
            setEditingLevel(undefined);
          }}
        />
      )}

      {isStageModalOpen && (
        <StageModal
          programId={id || ''}
          levelId={selectedStageLevelId}
          levelName={selectedStageLevelName}
          stage={editingStage}
          order={editingStage?.order || 1}
          onClose={() => {
            setIsStageModalOpen(false);
            setSelectedStageLevelId('');
            setSelectedStageLevelName('');
            setEditingStage(undefined);
          }}
        />
      )}

      {isSubjectModalOpen && (
        <SubjectModal
          programId={id || ''}
          stageId={selectedSubjectStageId}
          stageName={selectedSubjectStageName}
          subject={editingSubject}
          order={
            editingSubject?.order ??
            Math.max(
              0,
              ...allSubjectsInProgram
                .filter(subject => subject.stageId === selectedSubjectStageId)
                .map(subject => subject.order)
            ) + 1
          }
          availablePrerequisites={allSubjectsInProgram}
          prerequisiteLevels={program?.levels}
          onClose={() => {
            setIsSubjectModalOpen(false);
            setSelectedSubjectStageId('');
            setSelectedSubjectStageName('');
            setEditingSubject(undefined);
          }}
        />
      )}

      {isSessionModalOpen && (
        <SessionModal
          programId={id || ''}
          subjectId={selectedSessionSubjectId}
          subjectName={selectedSessionSubjectName}
          session={editingSession}
          sessionNumber={
            editingSession?.sessionNumber ??
            Math.max(
              0,
              ...(
                allSubjectsInProgram.find(subject => subject.id === selectedSessionSubjectId)
                  ?.sessions || []
              ).map(session => session.sessionNumber)
            ) + 1
          }
          onClose={() => {
            setIsSessionModalOpen(false);
            setSelectedSessionSubjectId('');
            setSelectedSessionSubjectName('');
            setEditingSession(undefined);
          }}
        />
      )}

      {isBulkSessionModalOpen && (
        <BulkSessionModal
          programId={id || ''}
          subjectId={selectedBulkSubjectId}
          subjectName={selectedBulkSubjectName}
          onClose={() => {
            setIsBulkSessionModalOpen(false);
            setSelectedBulkSubjectId('');
            setSelectedBulkSubjectName('');
          }}
        />
      )}

      {isFlowModalOpen && (
        <FlowModal
          programId={id || ''}
          levelId={selectedLevelId || ''}
          flow={editingFlow}
          order={editingFlow?.order || 1}
          onClose={() => {
            setIsFlowModalOpen(false);
            setSelectedLevelId('');
            setEditingFlow(undefined);
          }}
        />
      )}

      {isWhatsAppModalOpen && (
        <WhatsAppGroupModal
          group={editingWhatsAppGroup}
          initialProgramId={id || ''}
          onClose={() => setIsWhatsAppModalOpen(false)}
        />
      )}

      {isProgramModalOpen && (
        <ProgramModal program={program as Program} onClose={() => setIsProgramModalOpen(false)} />
      )}

      {isDuplicateProgramModalOpen && (
        <DuplicateProgramModal
          program={program as Program}
          onClose={() => setIsDuplicateProgramModalOpen(false)}
          onDuplicated={duplicatedProgram => {
            setIsDuplicateProgramModalOpen(false);
            addNotification({
              type: 'success',
              title: t(copy.programDetail.notifProgramDuplicatedTitle),
              message: t(copy.programDetail.notifProgramDuplicatedMessage),
            });
            navigate(`/programs/${duplicatedProgram.id}`);
          }}
        />
      )}

      {isManualEnrollModalOpen && (
        <ManualEnrollModal programId={id || ''} onClose={() => setIsManualEnrollModalOpen(false)} />
      )}

      {/* Detail Drawer */}
      {detailUserId && (
        <UserDetailDrawer
          userId={detailUserId}
          programId={id || undefined}
          onClose={() => setDetailUserId(null)}
        />
      )}
    </div>
  );
}
